import { useEffect, useState, useCallback } from "react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Bell, Check, Clock, Ban, RotateCcw, Sparkles, AlertTriangle } from "lucide-react";
import api from "@/lib/api";

function fmt(ts) {
  try { return new Date(ts).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return ts; }
}

export default function NotificationsBell() {
  const [data, setData] = useState({ unread: 0, notifications: [] });
  const [open, setOpen] = useState(false);

  const fetchNotifs = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setData(data);
    } catch {}
  }, []);

  useEffect(() => {
    fetchNotifs();
    const t = setInterval(fetchNotifs, 30000);
    const onRefresh = () => fetchNotifs();
    window.addEventListener("notifications:refresh", onRefresh);
    return () => { clearInterval(t); window.removeEventListener("notifications:refresh", onRefresh); };
  }, [fetchNotifs]);

  const markRead = async () => {
    try {
      await api.post("/notifications/read");
      setData((d) => ({ ...d, unread: 0, notifications: d.notifications.map((n) => ({ ...n, read: true })) }));
    } catch {}
  };

  const onOpenChange = (v) => {
    setOpen(v);
    if (v) fetchNotifs();
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" data-testid="notifications-bell-button" className="relative text-zinc-400 hover:text-white hover:bg-zinc-900">
          <Bell className="w-5 h-5" />
          {data.unread > 0 && (
            <span data-testid="notifications-unread-badge" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#FF3B30] text-white text-[10px] font-semibold flex items-center justify-center">
              {data.unread > 99 ? "99+" : data.unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0 bg-[#1A1A1A] border-white/10 text-white">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Bell className="w-4 h-4 text-[#007AFF]" />
            <span className="font-display font-medium text-sm">Notifications</span>
          </div>
          {data.unread > 0 && (
            <Button variant="ghost" size="sm" onClick={markRead} data-testid="notifications-mark-read-button" className="h-auto py-1 px-2 text-xs text-zinc-400 hover:text-white hover:bg-zinc-800">
              <Check className="w-3 h-3 mr-1" /> Tout marquer lu
            </Button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto" data-testid="notifications-list">
          {data.notifications.length === 0 ? (
            <div className="px-4 py-10 text-center text-zinc-500 text-sm">Aucune notification pour le moment.</div>
          ) : (
            data.notifications.map((n) => {
              const isResil = n.type === "resiliation";
              const isNouveau = n.type === "nouveau_site";
              const isRetard = n.type === "cles_retard" || n.type === "cr_bloque";
              const isBloque = n.type === "cr_bloque";
              const Icon = isRetard ? AlertTriangle : isNouveau ? Sparkles : isResil ? Ban : RotateCcw;
              const color = isRetard ? "text-[#FF3B30]" : isNouveau ? "text-[#FFB020]" : isResil ? "text-[#FF3B30]" : "text-emerald-400";
              return (
                <div key={n.notif_id} data-testid={`notification-${n.notif_id}`} className={`px-4 py-3 border-b border-white/5 last:border-0 ${!n.read ? "bg-[#007AFF]/5" : ""}`}>
                  <div className="flex items-start gap-2">
                    <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${color}`} />
                    <div className="min-w-0">
                      <p className="text-sm">{n.message}</p>
                      {isBloque && <a href="/dashboard/comptes-rendus-interventions" className="text-xs text-[#FFB020] hover:underline" data-testid={`notification-open-cr-${n.notif_id}`}>Ouvrir la file d'attente →</a>}
                      <p className="text-xs text-zinc-600 mt-1 flex items-center gap-1 font-code"><Clock className="w-3 h-3" /> {fmt(n.created_at)}</p>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
